// Allègement d'un fichier lourd — PDF ou photo — DANS LE NAVIGATEUR
// (04/11/2026), avant son dépôt.
//
// Pourquoi : un mode opératoire fournisseur, un rapport d'intervention scanné
// en 600 dpi couleur pèse vite 10 à 30 Mo pour un contenu qui se lit très bien
// à 150 dpi ; une photo de téléphone pèse 4 à 6 Mo pour 12 mégapixels dont
// l'écran n'affiche pas le quart. Stocker ce poids n'apporte rien, et il
// ralentit chaque ouverture.
//
// Comment :
//  · PDF — chaque page est redessinée en JPEG (pdf.js), puis réassemblée en
//    PDF (pdf-lib), page à page, au format d'origine ;
//  · photo — l'image est réduite (2 000 px de côté, 2 800 en « Nette ») et
//    réenregistrée en JPEG, orientation appliquée. Les données EXIF (dont la
//    position GPS du téléphone) ne sont pas recopiées.
// Rien ne part au serveur avant que l'utilisateur ait choisi : le fichier lourd
// ne remonte jamais, et une donnée de santé ne transite pas pour être compressée.
//
// ⚠️ La contrepartie, à dire à l'écran : le texte d'un PDF « natif » (produit
// par un logiciel) devient une image — il n'est plus sélectionnable ni
// recherchable. Pour un scan, il n'y avait pas de texte : on ne perd rien.
// C'est pourquoi `texte` est détecté et rendu, et pourquoi on ne compresse
// JAMAIS sans demander.
//
// Le bloc d'information du PDF (titre, auteur, dates…) est RECOPIÉ : le moteur
// le lit pour proposer le libellé et la date d'une pièce
// (`backend/src/pieces-metadonnees.ts`) — le perdre ferait retaper ce que le
// fichier déclarait.
//
// RÈGLE (04/11/2026) : tout champ de dépôt de Nafis passe par `allegerAuDepot`
// (store.ts), ou porte `data-sans-allegement="<motif>"`. Contrôle :
// `backend/tests/qualite-allegement-depots.test.js`. Exemptés aujourd'hui, parce
// que le serveur LIT le contenu : import CQI, comptes rendus MoMent / CASA,
// relevés de présence ; et ce dont la forme compte : aperçu animé (GIF),
// signature (PNG transparent).

/** Au-delà de ce poids, on propose d'alléger un PDF ou une photo. */
export const SEUIL_ALLEGEMENT = 1024 * 1024;

/** Un allègement qui ne gagne pas au moins 15 % ne vaut pas la perte de qualité. */
export const GAIN_MINIMAL = 0.15;

export type Qualite = 'standard' | 'nette';

/** Résolution et compression JPEG par qualité. 150 dpi se lit sans peine à
 *  l'écran et s'imprime proprement ; 200 dpi garde les petits caractères d'un
 *  tableau de valeurs ou d'une signature manuscrite. */
const REGLAGES: Record<Qualite, { dpi: number; jpeg: number }> = {
  standard: { dpi: 150, jpeg: 0.72 },
  nette: { dpi: 200, jpeg: 0.8 },
};

/** Côté le plus long d'une page rendue, en pixels : borne la mémoire pour un
 *  plan A0 ou une page mal déclarée. */
const COTE_MAX = 3000;

/** Côté le plus long d'une photo allégée, et sa compression JPEG. */
const REGLAGES_IMAGE: Record<Qualite, { cote: number; jpeg: number }> = {
  standard: { cote: 2000, jpeg: 0.8 },
  nette: { cote: 2800, jpeg: 0.86 },
};

export interface Allegement {
  fichier: File;
  avant: number;
  apres: number;
  /** Pages du PDF ; 1 pour une image. */
  pages: number;
}

export type Nature = 'pdf' | 'image';

/** Ce que l'on sait alléger, d'après le CONTENU, pas l'extension. Le GIF (qui
 *  peut être animé) et le HEIC (que la plupart des navigateurs ne savent pas
 *  lire) ne le sont pas. */
export async function natureAllegeable(fichier: File): Promise<Nature | null> {
  const t = new Uint8Array(await fichier.slice(0, 12).arrayBuffer());
  const txt = (d: number, f: number) => String.fromCharCode(...Array.from(t.slice(d, f)));
  if (txt(0, 5) === '%PDF-') return 'pdf';
  if (t[0] === 0xff && t[1] === 0xd8 && t[2] === 0xff) return 'image';
  if (t[0] === 0x89 && txt(1, 4) === 'PNG') return 'image';
  if (txt(0, 4) === 'RIFF' && txt(8, 12) === 'WEBP') return 'image';
  return null;
}

/** Dimensions d'une image, ou `null` si le navigateur ne sait pas la lire. */
export async function examinerImage(fichier: File): Promise<{ largeur: number; hauteur: number } | null> {
  try {
    const b = await createImageBitmap(fichier, { imageOrientation: 'from-image' });
    const r = { largeur: b.width, hauteur: b.height };
    b.close();
    return r;
  } catch {
    return null;
  }
}

/** Réduit une photo et la réenregistre en JPEG (nom en « .jpg »). */
export async function allegerImage(fichier: File, qualite: Qualite): Promise<Allegement> {
  const { cote, jpeg } = REGLAGES_IMAGE[qualite];
  const b = await createImageBitmap(fichier, { imageOrientation: 'from-image' }).catch(() => null);
  if (!b) throw new Error('Cette image n’a pas pu être lue pour être allégée.');
  const echelle = Math.min(1, cote / Math.max(b.width, b.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(b.width * echelle);
  canvas.height = Math.round(b.height * echelle);
  const ctx = canvas.getContext('2d');
  if (!ctx) { b.close(); throw new Error('Le navigateur n’a pas pu préparer l’image.'); }
  // Fond blanc : la transparence d'un PNG deviendrait noire en JPEG.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(b, 0, 0, canvas.width, canvas.height);
  b.close();
  const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/jpeg', jpeg));
  canvas.width = 0; canvas.height = 0;
  if (!blob) throw new Error('Le navigateur n’a pas pu compresser l’image.');
  const nom = fichier.name.replace(/\.[^.]*$/, '') + '.jpg';
  const allege = new File([blob], nom, { type: 'image/jpeg', lastModified: fichier.lastModified });
  return { fichier: allege, avant: fichier.size, apres: allege.size, pages: 1 };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PdfJs = any;
let pdfjsCharge: Promise<PdfJs> | null = null;

/** pdf.js n'est chargé qu'au premier PDF lourd : ~1 Mo qu'aucun autre écran n'a à payer. */
function pdfjs(): Promise<PdfJs> {
  if (!pdfjsCharge) {
    pdfjsCharge = import('pdfjs-dist').then((m) => {
      // `new Worker(new URL(…, import.meta.url))` : la forme que Turbopack
      // résout et publie lui-même — pas de copie du worker dans `public/` qui
      // divergerait de la version installée.
      m.GlobalWorkerOptions.workerPort = new Worker(new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url), { type: 'module' });
      return m;
    }).catch((e) => { pdfjsCharge = null; throw e; });
  }
  return pdfjsCharge;
}

/** Ouvre le PDF ; une erreur parlante si on ne peut pas (protégé, abîmé). */
async function ouvrir(fichier: File) {
  const lib = await pdfjs();
  try {
    const doc = await lib.getDocument({ data: new Uint8Array(await fichier.arrayBuffer()) }).promise;
    return { lib, doc };
  } catch (e) {
    if ((e as { name?: string })?.name === 'PasswordException') {
      throw new Error('Ce PDF est protégé par un mot de passe : il ne peut pas être allégé ici.');
    }
    throw new Error('Ce PDF n’a pas pu être lu pour être allégé.');
  }
}

/** Ce qu'on sait du PDF avant de proposer : nombre de pages, et s'il porte du
 *  texte (PDF natif) ou seulement des images (scan). Trois pages suffisent à
 *  trancher, et lire tout un manuel de 200 pages ferait attendre pour rien. */
export async function examinerPdf(fichier: File): Promise<{ pages: number; texte: boolean }> {
  const { doc } = await ouvrir(fichier);
  try {
    let caracteres = 0;
    for (let n = 1; n <= Math.min(3, doc.numPages); n++) {
      const page = await doc.getPage(n);
      const contenu = await page.getTextContent();
      for (const it of contenu.items as { str?: string }[]) caracteres += (it.str || '').trim().length;
      page.cleanup();
    }
    return { pages: doc.numPages, texte: caracteres > 40 };
  } finally {
    void doc.destroy();
  }
}

/** Rend une page en JPEG. */
async function pageEnJpeg(page: PdfJs, qualite: Qualite): Promise<{ octets: Uint8Array; largeur: number; hauteur: number }> {
  const { dpi, jpeg } = REGLAGES[qualite];
  // Dimensions d'origine en points (rotation comprise) : la page produite garde
  // exactement le format de la page lue.
  const base = page.getViewport({ scale: 1 });
  const echelle = Math.min(dpi / 72, COTE_MAX / Math.max(base.width, base.height));
  const vue = page.getViewport({ scale: echelle });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(vue.width);
  canvas.height = Math.ceil(vue.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Le navigateur n’a pas pu préparer la page.');
  // Fond blanc : un PDF sans fond deviendrait noir en JPEG.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, canvas, viewport: vue }).promise;
  const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/jpeg', jpeg));
  // Libère la mémoire du canevas tout de suite : sur un document de 80 pages,
  // attendre le ramasse-miettes fait monter l'onglet à plusieurs Go.
  canvas.width = 0; canvas.height = 0;
  if (!blob) throw new Error('Le navigateur n’a pas pu compresser une page.');
  return { octets: new Uint8Array(await blob.arrayBuffer()), largeur: base.width, hauteur: base.height };
}

/** Allège le PDF. `progres(n, total)` est appelé après chaque page. */
export async function allegerPdf(fichier: File, qualite: Qualite, progres?: (n: number, total: number) => void): Promise<Allegement> {
  const { lib, doc } = await ouvrir(fichier);
  try {
    const { PDFDocument } = await import('pdf-lib');
    // `updateMetadata: false` : pdf-lib n'écrit pas sa propre date ni son nom de
    // producteur — on recopie ceux de l'original juste après.
    const sortie = await PDFDocument.create({ updateMetadata: false });
    const total = doc.numPages;
    for (let n = 1; n <= total; n++) {
      const page = await doc.getPage(n);
      const { octets, largeur, hauteur } = await pageEnJpeg(page, qualite);
      page.cleanup();
      const image = await sortie.embedJpg(octets);
      sortie.addPage([largeur, hauteur]).drawImage(image, { x: 0, y: 0, width: largeur, height: hauteur });
      progres?.(n, total);
    }
    await recopierInfos(lib, doc, sortie);
    const octets = await sortie.save();
    const allege = new File([octets as BlobPart], fichier.name, { type: 'application/pdf', lastModified: fichier.lastModified });
    return { fichier: allege, avant: fichier.size, apres: allege.size, pages: total };
  } finally {
    void doc.destroy();
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function recopierInfos(lib: PdfJs, doc: PdfJs, sortie: any) {
  const info = ((await doc.getMetadata().catch(() => null))?.info || {}) as Record<string, unknown>;
  const txt = (v: unknown) => (typeof v === 'string' && v.trim() ? v : '');
  const date = (v: unknown) => (txt(v) ? lib.PDFDateString?.toDateObject(v) as Date | null : null);
  const discret = { showInWindowTitleBar: false };
  if (txt(info.Title)) sortie.setTitle(info.Title, discret);
  if (txt(info.Author)) sortie.setAuthor(info.Author);
  if (txt(info.Subject)) sortie.setSubject(info.Subject);
  if (txt(info.Keywords)) sortie.setKeywords([info.Keywords as string]);
  if (txt(info.Creator)) sortie.setCreator(info.Creator);
  if (txt(info.Producer)) sortie.setProducer(info.Producer);
  const creation = date(info.CreationDate);
  const modification = date(info.ModDate);
  if (creation) sortie.setCreationDate(creation);
  if (modification) sortie.setModificationDate(modification);
}
